import { useAuth } from '../hooks/useAuth'

/**
 * Why a sign-in failed, as the fixed reason code the backend's failure handler redirects back
 * with (see SecurityConfig.signInFailureHandler). The wording lives here, owned by the UI.
 */
const SIGNIN_ERRORS: Record<string, { title: string; body: string }> = {
  no_role: {
    title: 'Your account has no access yet',
    body:
      'Your Microsoft sign-in worked, but no role is assigned to your account for this ' +
      'application. Ask an administrator to assign you a role (Viewer, Researcher or Admin) ' +
      'in Entra ID, then sign in again.',
  },
  tenant: {
    title: 'That account belongs to a different organisation',
    body: 'Only Burendo Microsoft accounts can access this application. Sign in with your Burendo account.',
  },
  failed: {
    title: 'Sign-in didn’t complete',
    body: 'Something interrupted the Microsoft sign-in. Please try again — if it keeps happening, contact an administrator.',
  },
}

/**
 * The whole login surface. There is no password field, and never will be — Microsoft Entra ID
 * authenticates the user and this app only ever sees the result. The prototype had no auth at
 * all (problem #3 of the rebuild); this screen is the fix.
 */
export function SignIn() {
  const { me, signIn } = useAuth()
  const devMode = !!me?.devMode
  const errorCode = new URLSearchParams(window.location.search).get('signin_error')
  const signinError = errorCode ? (SIGNIN_ERRORS[errorCode] ?? SIGNIN_ERRORS.failed) : null

  return (
    <div className="signin">
      <div className="signin-card">
        <div className="signin-brand">
          Burendo <span className="oip">OIP</span>
        </div>
        <div className="signin-sub">Opportunity Intelligence Platform</div>

        <h1 className="signin-title">Sign in</h1>
        <p className="signin-lede">
          {devMode
            ? 'Development mode — no Microsoft account required.'
            : 'Use your Burendo Microsoft account. No separate password to create or remember.'}
        </p>

        {signinError && (
          <div className="err" role="alert">
            <b>{signinError.title}.</b> {signinError.body}
          </div>
        )}

        {/* Never let anyone mistake the dev stub for real SSO. */}
        {devMode && (
          <div className="err" role="alert">
            <b>Stub authentication is active</b> (<code>AUTH_MODE=local</code>). Anyone who clicks below is
            admitted with no credential check. Never run this outside local development.
          </div>
        )}

        <button className="btn-signin" onClick={signIn}>
          {devMode ? 'Sign in as dev user' : 'Continue with Microsoft'}
        </button>

        <p className="note">
          {devMode
            ? 'To use real Microsoft SSO, set AUTH_MODE=entra and fill in the Entra values. See docs/AUTH.md.'
            : 'Access is granted by role in Entra ID. If you sign in and see a permissions error, ask an administrator to assign you a role for this application.'}
        </p>
      </div>
    </div>
  )
}
